import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import "./notizbloecke.css";
import { alleFenster, aktiveAuswahl, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * ══ DER PLATZ DER AUSWAHLLISTEN HINTER `@` UND `#` ═════════════════════════
 * Eine Hülle, die `BezugPicker` bzw. `MentionPicker` an den Cursor stellt —
 * als PORTAL am `body` und `position: fixed`.
 *
 * Warum nicht einfach `absolute` in einer Hülle um das Schreibfeld: die
 * Notizfläche steht nicht immer in einer Karte, die über ihren Rand hinaus
 * zeichnen darf. Im Kundentagebuch sitzt sie in einer `.vgd-card`, und die
 * trägt `overflow: hidden` (`vorgangDetail.css`) — die Eingabezeile ist dort
 * das LETZTE Element im Kartenkörper, unter ihr liegen nur noch 2 px
 * Flächen- und 18 px Kartenpolster. In Chrome gemessen blieben von einer
 * Auswahl, die unter der Zeile aufgeht, 21 px sichtbar — bei voller Liste
 * 21 von 280 px. Dasselbe gilt für jeden Block am unteren Kartenrand.
 *
 * `position: fixed` in einem Portal am `body` hat keinen klippenden Vorfahren
 * mehr — die Auswahl ist in JEDER Fläche vollständig sichtbar, egal wo sie
 * eingebettet ist. Bezahlt wird das damit, dass die Hülle ihre Position
 * selbst rechnen muss; genau das tut `stellen()`.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DIE MARKIERUNG BLEIBT, WO SIE IST ══════════════════════════════════════
 * Die Hülle nimmt keinen Fokus und hört auf keine Tasten. Der Cursor bleibt im
 * `contentEditable`, die Listen verhindern in ihren Einträgen das
 * `mousedown`-Default — die Stelle, an der der Bezug eingesetzt wird, überlebt
 * den Klick. Ein Portal ändert daran nichts: React lässt seine Ereignisse
 * weiter am React-Baum entlang blubbern, nicht am DOM-Baum.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Luft zwischen der Cursorzeile und der Auswahl. */
const ABSTAND = 4;
/** Mindestabstand zum Fensterrand — wie im Rechtsklick-Menü. */
const RAND = 8;

/**
 * Woran die Auswahl hängt: das Rechteck des Cursors, sonst das des Feldes.
 *
 * Ein zusammengefallener Cursor liefert ein Rechteck der Breite 0 — das ist
 * richtig so und die genaueste Angabe, die es gibt. Nur ein RUNDUM leeres
 * Rechteck ist keine Auskunft (frisch geleertes Feld); dann steht die Auswahl
 * unter dem ganzen Feld, so wie früher.
 *
 * `getBoundingClientRect` wird am `Range` GEPRÜFT, nicht vorausgesetzt: jsdom
 * kennt die Methode nicht, und ohne diese Abfrage flöge jeder Test in die
 * Ausnahme statt das Feld als Rückfall zu nehmen.
 */
function ankerRechteck(feld: HTMLElement | null): DOMRect | null {
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    const knoten = sel?.anchorNode ?? null;
    if (sel && sel.rangeCount > 0 && knoten && feld?.contains(knoten)) {
        const bereich = sel.getRangeAt(0);
        const r =
            typeof bereich.getBoundingClientRect === "function"
                ? bereich.getBoundingClientRect()
                : null;
        if (r && (r.height > 0 || r.width > 0 || r.top > 0 || r.left > 0)) return r;
    }
    return feld?.getBoundingClientRect() ?? null;
}

export const AuswahlAnker: React.FC<{
    /** Das `contentEditable`, in dem getippt wird. */
    feld: HTMLElement | null;
    children: React.ReactNode;
}> = ({ feld, children }) => {
    const ref = useRef<HTMLDivElement | null>(null);

    /**
     * Unter den Cursor stellen — und nur dann darüber, wenn unten wirklich zu
     * wenig Platz ist und oben mehr. Geschrieben wird direkt in den Stil des
     * Knotens statt in einen State: die Liste wird mit jedem Buchstaben
     * kürzer, ein State-Umweg brauchte dafür einen zweiten Renderdurchgang.
     */
    const stellen = useCallback(() => {
        const el = ref.current;
        const anker = ankerRechteck(feld);
        if (!el || !anker) return;
        const eigen = el.getBoundingClientRect();
        const platzUnten = aktivesFenster().innerHeight - anker.bottom - RAND;
        const platzOben = anker.top - RAND;
        const nachOben = eigen.height > platzUnten && platzOben > platzUnten;
        const top = nachOben
            ? anker.top - eigen.height - ABSTAND
            : anker.bottom + ABSTAND;
        // Die Klemmung gewinnt über die Wunschposition: lieber überdeckt die
        // Auswahl ein Stück Text, als dass sie aus dem Bild läuft.
        el.style.top = `${Math.max(RAND, Math.min(top, aktivesFenster().innerHeight - eigen.height - RAND))}px`;
        el.style.left = `${Math.max(RAND, Math.min(anker.left, aktivesFenster().innerWidth - eigen.width - RAND))}px`;
    }, [feld]);

    // Nach JEDEM Render neu rechnen (kein Abhängigkeitsfeld): der Cursor
    // wandert beim Tippen, und die Trefferliste ändert dabei ihre Höhe.
    // `useLayoutEffect` läuft vor dem Zeichnen — die Auswahl erscheint sofort
    // an der richtigen Stelle, nicht erst in der linken oberen Ecke.
    useLayoutEffect(stellen);

    // Scrollen und Größenänderung verschieben den Cursor, nicht aber ein
    // `fixed` Kästchen. Nachziehen statt schließen: geschlossen würde die
    // Auswahl schon beim Nachrücken der Zeile verschwinden, die der Browser
    // beim Tippen selbst ins Bild scrollt.
    useEffect(() => {
        alleFenster.addEventListener("resize", stellen);
        // `capture`, damit auch das Scrollen eines inneren Bereichs zählt.
        alleFenster.addEventListener("scroll", stellen, true);
        return () => {
            alleFenster.removeEventListener("resize", stellen);
            alleFenster.removeEventListener("scroll", stellen, true);
        };
    }, [stellen]);

    return createPortal(
        // `kvp-rd` bringt die Schriftfamilie mit — am `body` gäbe es sie
        // sonst nicht, und die Einträge stehen auf `font: inherit`. Den
        // `min-height: 100%`-Riegel dieser Klasse nimmt `.nb-picker-anker`
        // wieder weg (siehe CSS), sonst wäre die Hülle bildschirmhoch.
        // `data-popover-teil`: Ein Popover oder Faden, in dem das Feld steht,
        // zählt einen Klick hierher nicht als „daneben" (siehe `RdPopover`).
        <div ref={ref} className="kvp-rd nb-picker-anker" data-popover-teil="">
            {children}
        </div>,
        document.body,
    );
};
